/**
 * Admin RTK Query — endpoints /admin/* du Worker (rôle admin/moderator).
 * La décision de vérification est appliquée côté serveur (audit log inclus).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { env } from '../../config/env';
import { loadStoredToken } from '../../features/auth/model/session.slice';
import type { AdminVerificationItem } from '../../types';

async function adminRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = loadStoredToken();
  const res = await fetch(`${env.apiUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const body = (await res.json().catch(() => null)) as { data?: T; error?: { message?: string } } | null;
  if (!res.ok || body?.error) {
    throw new Error(body?.error?.message ?? `Erreur API (${res.status})`);
  }
  return body?.data as T;
}

export interface AdminStats {
  activeProfessionals: number;
  voiceMinutesUsed: number;
  qualificationRate: number;
}

export interface AdminAuditRow {
  time: string;
  user: string;
  action: string;
  status: string;
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: async () => ({ data: null as unknown }),
  tagTypes: ['AdminVerifications', 'AdminStats', 'AdminAudit'],
  endpoints: (builder) => ({
    getVerifications: builder.query<AdminVerificationItem[], void>({
      queryFn: async () => {
        try {
          return { data: await adminRequest<AdminVerificationItem[]>('/admin/verifications') };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['AdminVerifications'],
    }),
    decideVerification: builder.mutation<{ id: string; status: string }, { id: string; decision: 'approve' | 'reject' }>({
      queryFn: async ({ id, decision }) => {
        try {
          return {
            data: await adminRequest<{ id: string; status: string }>(`/admin/verifications/${id}/decision`, {
              method: 'POST',
              body: JSON.stringify({ decision }),
            }),
          };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['AdminVerifications', 'AdminStats'],
    }),
    getStats: builder.query<AdminStats, void>({
      queryFn: async () => {
        try {
          return { data: await adminRequest<AdminStats>('/admin/stats') };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['AdminStats'],
    }),
    getAudit: builder.query<AdminAuditRow[], void>({
      queryFn: async () => {
        try {
          return { data: await adminRequest<AdminAuditRow[]>('/admin/audit') };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['AdminAudit'],
    }),
  }),
});

export const { useGetVerificationsQuery, useDecideVerificationMutation, useGetStatsQuery, useGetAuditQuery } = adminApi;
